import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { IoMdClose } from "react-icons/io";
import { FaFilePdf, FaFileWord } from "react-icons/fa";
import { GetVendorCategoryListDropDownSlice } from "../../../../redux/features/VendorCategoryManagementSlice";
import { GetVendorCityListDropDownSlice } from "../../../../redux/features/VendorCityManagementSlice";
import { GetVendorDetailSlice, UpdateVendorSlice } from "../../../../redux/features/VendorManagementSlice";
import { onImageHandler, onDocumentHandler } from "../../../../components/UploadImageOnBucket";
import ButtonSpinner from "../../../../common/Loader/ButtonSpinner";

const emptyPackage = () => ({
  packageName: "",
  description: "",
  approximatePrice: "",
  lowestPrice: "",
  workIncludes: "",
  images: [],
  documents: [],
});

const EditVendor = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();

  const { loading } = useSelector((state) => state.VendorManagement);

  const [categoryOptions, setCategoryOptions] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);
  const [ready, setReady] = useState(false);

  const [basicInfo, setBasicInfo] = useState({
    businessName: "",
    ownerName: "",
    contactPersonName: "",
    mobile1: "",
    mobile2: "",
    city: "",
    description: "",
    address: "",
    pincode: "",
    isActive: true,
  });

  const [selectedCategoryIds, setSelectedCategoryIds] = useState([]);
  const [categoryPackages, setCategoryPackages] = useState({});

  useEffect(() => {
    dispatch(GetVendorCategoryListDropDownSlice()).then((res) => {
      setCategoryOptions(res?.payload?.data || []);
    });
    dispatch(GetVendorCityListDropDownSlice()).then((res) => {
      setCityOptions(res?.payload?.data || []);
    });

    dispatch(GetVendorDetailSlice(id)).then((res) => {
      const vendor = res?.payload?.data;
      if (!vendor) return;

      setBasicInfo({
        businessName: vendor.businessName || "",
        ownerName: vendor.ownerName || "",
        contactPersonName: vendor.contactPersonName || "",
        mobile1: vendor.mobile1 || "",
        mobile2: vendor.mobile2 || "",
        city: vendor.city?._id || "",
        description: vendor.description || "",
        address: vendor.address || "",
        pincode: vendor.pincode || "",
        isActive: vendor.isActive ?? true,
      });

      const catIds = [];
      const catPackages = {};
      const extraCategoryOptions = [];

      (vendor.categories || []).forEach((c) => {
        const catId = c.categoryId?._id;
        if (!catId) return;

        catIds.push(catId);
        catPackages[catId] = (c.packages || []).map((p) => ({
          packageName: p.packageName || "",
          description: p.description || "",
          approximatePrice: p.approximatePrice ?? "",
          lowestPrice: p.lowestPrice ?? "",
          workIncludes: p.workIncludes || "",
          images: p.images || [],
          documents: p.documents || [],
        }));
        extraCategoryOptions.push(c.categoryId);
      });

      setSelectedCategoryIds(catIds);
      setCategoryPackages(catPackages);

      setCategoryOptions((prev) => {
        const existingIds = new Set(prev.map((x) => x._id));
        const merged = [...prev];
        extraCategoryOptions.forEach((opt) => {
          if (opt && !existingIds.has(opt._id)) merged.push(opt);
        });
        return merged;
      });

      if (vendor.city) {
        setCityOptions((prev) =>
          prev.some((x) => x._id === vendor.city._id) ? prev : [...prev, vendor.city]
        );
      }

      setReady(true);
    });
  }, [id]);

  const handleBasicChange = (e) => {
    const { name, value } = e.target;
    setBasicInfo((prev) => ({ ...prev, [name]: value }));
  };

  const handleStatusToggle = () => {
    setBasicInfo((prev) => ({ ...prev, isActive: !prev.isActive }));
  };

  const toggleCategory = (categoryId) => {
    setSelectedCategoryIds((prev) => {
      if (prev.includes(categoryId)) {
        return prev.filter((id) => id !== categoryId);
      }
      return [...prev, categoryId];
    });

    setCategoryPackages((prev) => {
      if (prev[categoryId]) return prev;
      return { ...prev, [categoryId]: [emptyPackage()] };
    });
  };

  const addPackage = (categoryId) => {
    setCategoryPackages((prev) => ({
      ...prev,
      [categoryId]: [...(prev[categoryId] || []), emptyPackage()],
    }));
  };

  const removePackage = (categoryId, pkgIndex) => {
    setCategoryPackages((prev) => ({
      ...prev,
      [categoryId]: prev[categoryId].filter((_, i) => i !== pkgIndex),
    }));
  };

  const handlePackageFieldChange = (categoryId, pkgIndex, field, value) => {
    setCategoryPackages((prev) => ({
      ...prev,
      [categoryId]: prev[categoryId].map((pkg, i) =>
        i === pkgIndex ? { ...pkg, [field]: value } : pkg
      ),
    }));
  };

  const handlePackageImageUpload = async (e, categoryId, pkgIndex) => {
    const files = Array.from(e.target.files || []);
    for (const file of files) {
      try {
        const result = await onImageHandler({ target: { files: [file] } });
        if (Array.isArray(result)) {
          const url = result[1]?.location;
          setCategoryPackages((prev) => ({
            ...prev,
            [categoryId]: prev[categoryId].map((pkg, i) =>
              i === pkgIndex ? { ...pkg, images: [...pkg.images, url] } : pkg
            ),
          }));
        } else {
          toast.error(result);
        }
      } catch (error) {
        toast.error("Something went wrong while uploading image on bucket!");
      }
    }
    e.target.value = null;
  };

  const removePackageImage = (categoryId, pkgIndex, imgIndex) => {
    setCategoryPackages((prev) => ({
      ...prev,
      [categoryId]: prev[categoryId].map((pkg, i) =>
        i === pkgIndex
          ? { ...pkg, images: pkg.images.filter((_, idx) => idx !== imgIndex) }
          : pkg
      ),
    }));
  };

  const handlePackageDocumentUpload = async (e, categoryId, pkgIndex) => {
    const files = Array.from(e.target.files || []);
    for (const file of files) {
      try {
        const result = await onDocumentHandler({ target: { files: [file] } });
        if (Array.isArray(result)) {
          const doc = { url: result[1]?.location, name: file.name, fileType: file.type };
          setCategoryPackages((prev) => ({
            ...prev,
            [categoryId]: prev[categoryId].map((pkg, i) =>
              i === pkgIndex ? { ...pkg, documents: [...pkg.documents, doc] } : pkg
            ),
          }));
        } else {
          toast.error(result);
        }
      } catch (error) {
        toast.error("Something went wrong while uploading document on bucket!");
      }
    }
    e.target.value = null;
  };

  const removePackageDocument = (categoryId, pkgIndex, docIndex) => {
    setCategoryPackages((prev) => ({
      ...prev,
      [categoryId]: prev[categoryId].map((pkg, i) =>
        i === pkgIndex
          ? { ...pkg, documents: pkg.documents.filter((_, idx) => idx !== docIndex) }
          : pkg
      ),
    }));
  };

  const handleSubmit = () => {
    if (!basicInfo.businessName?.trim()) {
      toast.error("Please fill vendor business name!");
      return;
    }
    if (!basicInfo.ownerName?.trim()) {
      toast.error("Please fill owner name!");
      return;
    }
    if (!basicInfo.contactPersonName?.trim()) {
      toast.error("Please fill contact person name!");
      return;
    }
    if (!basicInfo.mobile1?.trim()) {
      toast.error("Please fill mobile number!");
      return;
    }
    if (!basicInfo.city) {
      toast.error("Please select city!");
      return;
    }
    if (selectedCategoryIds.length === 0) {
      toast.error("Please select at least one category!");
      return;
    }

    const categoriesPayload = selectedCategoryIds.map((categoryId) => ({
      categoryId,
      packages: (categoryPackages[categoryId] || [])
        .filter((p) => p.packageName?.trim())
        .map((p) => ({
          packageName: p.packageName,
          description: p.description,
          approximatePrice: Number(p.approximatePrice) || 0,
          lowestPrice: Number(p.lowestPrice) || 0,
          workIncludes: p.workIncludes,
          images: p.images,
          documents: p.documents,
        })),
    }));

    const payload = { ...basicInfo, categories: categoriesPayload };

    dispatch(UpdateVendorSlice({ id, data: payload })).then((res) => {
      if (res?.payload?.data) {
        navigate("/VendorManagement/Vendors");
      }
    });
  };

  if (!ready) {
    return (
      <div className="WrapperArea">
        <div className="WrapperBox">
          <div className="TitleBox">
            <h2>Loading vendor...</h2>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="WrapperArea">
      <div className="WrapperBox">
        <div className="TitleBox">
          <h2>Edit Vendor</h2>
        </div>

        <div className="Small-Wrapper">
          <div className="TitleBox">
            <h2>Basic Vendor Information</h2>
          </div>
          <div className="ItineraryArea">
            <div className="row">
              <div className="col-sm-6">
                <div className="form-group">
                  <label>Vendor Business Name</label>
                  <input
                    type="text"
                    className="form-control"
                    name="businessName"
                    value={basicInfo.businessName}
                    onChange={handleBasicChange}
                  />
                </div>
              </div>
              <div className="col-sm-6">
                <div className="form-group">
                  <label>Owner Name</label>
                  <input
                    type="text"
                    className="form-control"
                    name="ownerName"
                    value={basicInfo.ownerName}
                    onChange={handleBasicChange}
                  />
                </div>
              </div>
              <div className="col-sm-6">
                <div className="form-group">
                  <label>Contact Person Name</label>
                  <input
                    type="text"
                    className="form-control"
                    name="contactPersonName"
                    value={basicInfo.contactPersonName}
                    onChange={handleBasicChange}
                  />
                </div>
              </div>
              <div className="col-sm-6">
                <div className="form-group">
                  <label>City</label>
                  <select
                    className="form-control"
                    name="city"
                    value={basicInfo.city}
                    onChange={handleBasicChange}
                  >
                    <option value="">--Select City--</option>
                    {Array.isArray(cityOptions) &&
                      cityOptions?.map((item) => (
                        <option key={item?._id} value={item?._id}>
                          {item?.cityName}, {item?.state}
                        </option>
                      ))}
                  </select>
                </div>
              </div>
              <div className="col-sm-6">
                <div className="form-group">
                  <label>Mobile Number 1</label>
                  <input
                    type="text"
                    className="form-control"
                    name="mobile1"
                    value={basicInfo.mobile1}
                    onChange={handleBasicChange}
                  />
                </div>
              </div>
              <div className="col-sm-6">
                <div className="form-group">
                  <label>Mobile Number 2</label>
                  <input
                    type="text"
                    className="form-control"
                    name="mobile2"
                    value={basicInfo.mobile2}
                    onChange={handleBasicChange}
                  />
                </div>
              </div>
              <div className="col-sm-6">
                <div className="form-group">
                  <label>Pincode</label>
                  <input
                    type="text"
                    className="form-control"
                    name="pincode"
                    value={basicInfo.pincode}
                    onChange={handleBasicChange}
                  />
                </div>
              </div>
              <div className="col-sm-6">
                <div className="form-group">
                  <label>Vendor Status</label>
                  <div
                    onClick={handleStatusToggle}
                    style={{ display: "flex", alignItems: "center", gap: "10px", cursor: "pointer" }}
                  >
                    <div
                      style={{
                        width: "50px",
                        height: "26px",
                        borderRadius: "20px",
                        backgroundColor: basicInfo.isActive ? "#28a745" : "#ccc",
                        position: "relative",
                        transition: "0.3s",
                      }}
                    >
                      <div
                        style={{
                          width: "22px",
                          height: "22px",
                          borderRadius: "50%",
                          backgroundColor: "#fff",
                          position: "absolute",
                          top: "2px",
                          left: basicInfo.isActive ? "26px" : "2px",
                          transition: "0.3s",
                          boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
                        }}
                      />
                    </div>
                    <span>{basicInfo.isActive ? "Active" : "Inactive"}</span>
                  </div>
                </div>
              </div>
              <div className="col-sm-12">
                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    className="form-control"
                    rows={2}
                    name="description"
                    value={basicInfo.description}
                    onChange={handleBasicChange}
                  />
                </div>
              </div>
              <div className="col-sm-12">
                <div className="form-group">
                  <label>Address</label>
                  <textarea
                    className="form-control"
                    rows={2}
                    name="address"
                    value={basicInfo.address}
                    onChange={handleBasicChange}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="Small-Wrapper">
          <div className="TitleBox">
            <h2>Categories</h2>
          </div>
          <div className="ItineraryArea">
            <div style={{ display: "flex", flexWrap: "wrap", gap: "20px" }}>
              {Array.isArray(categoryOptions) &&
                categoryOptions?.map((item) => (
                  <label
                    key={item?._id}
                    style={{ display: "flex", alignItems: "center", gap: "6px", cursor: "pointer" }}
                  >
                    <input
                      type="checkbox"
                      checked={selectedCategoryIds.includes(item?._id)}
                      onChange={() => toggleCategory(item?._id)}
                    />
                    {item?.categoryName}
                  </label>
                ))}
            </div>
          </div>
        </div>

        {selectedCategoryIds.map((categoryId) => {
          const category = categoryOptions?.find((c) => c._id === categoryId);
          const packages = categoryPackages[categoryId] || [];

          return (
            <div className="Small-Wrapper" key={categoryId}>
              <div className="TitleBox">
                <h2>{category?.categoryName} - Packages</h2>
              </div>

              {packages.map((pkg, pkgIndex) => (
                <div
                  key={pkgIndex}
                  style={{
                    border: "1px solid lightgray",
                    borderRadius: "10px",
                    padding: "15px",
                    marginBottom: "20px",
                    position: "relative",
                  }}
                >
                  <a
                    href="javascript:void(0);"
                    onClick={() => removePackage(categoryId, pkgIndex)}
                    style={{ position: "absolute", right: "10px", top: "10px", color: "red" }}
                  >
                    <i className="fa fa-trash" /> Remove Package
                  </a>
                  <div className="ItineraryArea">
                    <div className="row">
                      <div className="col-sm-6">
                        <div className="form-group">
                          <label>Package Name</label>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="e.g. Basic Catering"
                            value={pkg.packageName}
                            onChange={(e) =>
                              handlePackageFieldChange(categoryId, pkgIndex, "packageName", e.target.value)
                            }
                          />
                        </div>
                      </div>
                      <div className="col-sm-3">
                        <div className="form-group">
                          <label>Approximate Price (₹)</label>
                          <input
                            type="number"
                            className="form-control"
                            value={pkg.approximatePrice}
                            onChange={(e) =>
                              handlePackageFieldChange(categoryId, pkgIndex, "approximatePrice", e.target.value)
                            }
                          />
                        </div>
                      </div>
                      <div className="col-sm-3">
                        <div className="form-group">
                          <label>Lowest Price (₹)</label>
                          <input
                            type="number"
                            className="form-control"
                            value={pkg.lowestPrice}
                            onChange={(e) =>
                              handlePackageFieldChange(categoryId, pkgIndex, "lowestPrice", e.target.value)
                            }
                          />
                        </div>
                      </div>
                      <div className="col-sm-12">
                        <div className="form-group">
                          <label>Description</label>
                          <textarea
                            className="form-control"
                            rows={2}
                            value={pkg.description}
                            onChange={(e) =>
                              handlePackageFieldChange(categoryId, pkgIndex, "description", e.target.value)
                            }
                          />
                        </div>
                      </div>
                      <div className="col-sm-12">
                        <div className="form-group">
                          <label>Work Includes</label>
                          <textarea
                            className="form-control"
                            rows={2}
                            placeholder="e.g. Food + Staff"
                            value={pkg.workIncludes}
                            onChange={(e) =>
                              handlePackageFieldChange(categoryId, pkgIndex, "workIncludes", e.target.value)
                            }
                          />
                        </div>
                      </div>
                      <div className="col-sm-6">
                        <div className="form-group">
                          <label>Upload Images</label>
                          <div className="UploadBox">
                            <div className="Upload">
                              <i className="fa fa-upload" /> <span>Upload</span>
                              <input
                                type="file"
                                multiple
                                accept=".jpg,.png,.jpeg"
                                onChange={(e) => handlePackageImageUpload(e, categoryId, pkgIndex)}
                              />
                            </div>
                          </div>
                          <div style={{ display: "flex", flexWrap: "wrap", gap: "10px", marginTop: "10px" }}>
                            {pkg.images?.map((url, imgIndex) => (
                              <div
                                key={imgIndex}
                                style={{ position: "relative", width: "80px", height: "80px" }}
                              >
                                <img
                                  src={url}
                                  alt=""
                                  style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "6px" }}
                                />
                                <IoMdClose
                                  onClick={() => removePackageImage(categoryId, pkgIndex, imgIndex)}
                                  style={{
                                    position: "absolute",
                                    top: "-8px",
                                    right: "-8px",
                                    background: "white",
                                    border: "1px solid",
                                    borderRadius: "50%",
                                    cursor: "pointer",
                                  }}
                                />
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                      <div className="col-sm-6">
                        <div className="form-group">
                          <label>Upload Documents (Price List / Menu / Brochure)</label>
                          <div className="UploadBox">
                            <div className="Upload">
                              <i className="fa fa-upload" /> <span>Upload</span>
                              <input
                                type="file"
                                multiple
                                accept=".pdf,.doc,.docx"
                                onChange={(e) => handlePackageDocumentUpload(e, categoryId, pkgIndex)}
                              />
                            </div>
                          </div>
                          <div style={{ display: "flex", flexDirection: "column", gap: "6px", marginTop: "10px" }}>
                            {pkg.documents?.map((doc, docIndex) => (
                              <div
                                key={docIndex}
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "8px",
                                  border: "1px solid lightgray",
                                  borderRadius: "6px",
                                  padding: "5px 10px",
                                }}
                              >
                                {doc.fileType === "application/pdf" ? (
                                  <FaFilePdf color="red" />
                                ) : (
                                  <FaFileWord color="blue" />
                                )}
                                <a href={doc.url} target="_blank" rel="noreferrer" style={{ flex: 1 }}>
                                  {doc.name}
                                </a>
                                <IoMdClose
                                  style={{ cursor: "pointer" }}
                                  onClick={() => removePackageDocument(categoryId, pkgIndex, docIndex)}
                                />
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              <button
                type="button"
                className="Button"
                onClick={() => addPackage(categoryId)}
              >
                + Add Another Package
              </button>
            </div>
          );
        })}

        <button disabled={loading} onClick={handleSubmit} className="Button" style={{ marginTop: "10px" }}>
          {loading ? <ButtonSpinner /> : "Save Changes"}
        </button>
      </div>
    </div>
  );
};

export default EditVendor;
